import {
	Avatar,
	Button,
	Checkbox,
	DatePicker,
	Modal,
	Pagination,
	Select,
	Space,
	Spin,
	Table,
	Tooltip,
	Typography,
	Upload,
} from "antd";
import { getProfessorById } from "../../Requests/academic-user-requests";
import { StatusCodes } from "http-status-codes";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import {
	useGetCompanyId,
	getUserId,
	useIsProfessor,
	useIsCompany,
	isUserLogged,
	useIsFacultyAdmin,
	useIsDean,
	useIsInternshipAdmin,
	useIsAdmin,
} from "../../utils/utilFunctions";
import { getCompanyById } from "../../Requests/company-requests";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { getCompanyUserById } from "../../Requests/company-users-requests";
import { useTranslation } from "react-i18next";
import {
	CompanyDocumentStatusEnum,
	DocumentStatusEnum,
	ErrorCodes,
	InternshipType,
	ProfessionalTitleEnum,
	Role,
	DocumentTypeEnum,
} from "../../Api";
import { theme } from "../../theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { brands, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import CustomButton from "../../CustomComponents/CustomButton";
import { useQuery, useQueryClient } from "react-query";
import {
	getCompany,
	getCompanyUser,
	getDocuments,
	getProfessor,
} from "../../utils/reactQueriesConstants";
import PracticeSupervisorTable from "./PracticeSupervisorTable";
import SupervisedStudyProgramsTable from "./SupervisedStudyProgramsTable";
import FacultiesOfInterestTable from "./FacultiesOfInterestTable";
import ColaborationHistoryTable from "./ColaborationHistoryTable";
import {
	addNewDocument,
	deleteDocument,
	downloadDocument,
	getCompanyDocuments,
	rejectDocument,
	updateDocumentExpiration,
	updateSkipOfferValidationStatus,
} from "../../Requests/company-document-requests";
import { useCallback, useState } from "react";
import CustomModalFooter from "../../Containers/CustomModalFooter";
import { getErrorFromResponse } from "../../utils/responseUtils";
import CustomEditor from "../../CustomComponents/CustomEditor";
import dayjs from "../../utils/dayjs-setup";
import styles from "./Profile.module.scss";
import { downloadFile } from "../../utils/downloadUtils";
import ConfirmationModal from "../../Containers/ConfirmationModal";
import useQueryFilters from "../../Hooks/useQueryFilters";
import DashboardLayout from "../../layouts/Dashboard";
import { ArrowDownOutlined, UserOutlined } from "@ant-design/icons";

const Profile = () => {
	const { t, i18n } = useTranslation();
	const location = useLocation<
		| {
				id: string;
				type: InternshipType;
				userType: Role;
				filters: any;
				searchTerm: string;
				sortField: any;
				sortDirection: any;
				currentPage: any;
				pageSize: any;
				activeTab: string;
				origin: string;
		  }
		| null
		| undefined
	>();
	const isProfessor = useIsProfessor();
	const isCompany = useIsCompany();
	const isFacultyAdmin = useIsFacultyAdmin();
	const isDean = useIsDean();
	const isInternshipAdmin = useIsInternshipAdmin();
	const isAdmin = useIsAdmin();
	const companyId = useGetCompanyId();
	const [documentsOptions, setDocumentsOptions] = useState<any>([]);
	const [documentTypes, setDocumentTypes] = useState([]);
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrent] = useState(1);
	const [validity, setValidity] = useState<DocumentStatusEnum>(
		DocumentStatusEnum.All
	);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalVisibility, setModalVisibility] = useState(false);
	const [spinning, setSpinning] = useState(false);
	const [file, setFile] = useState<Blob>();
	const [documentType, setDocumentType] = useState<any>(null);
	const [selectedFaculty, setSelectedFaculty] = useState(undefined);
	const [selectedDocumentId, setSelectedDocumentId] = useState<any>(undefined);
	const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
	const [rejectionMessage, setRejectionMessage] = useState("");
	const queryClient = useQueryClient();
	const [adminFaculties, setAdminFaculties] = useState<any>([]);
	const query = useQueryFilters({});
	const history = useHistory();
	const [expiresAt, setExpiresAt] = useState<any>(null);
	const [updateDoc, setUpdateDoc] = useState<any | null>(null);
	const [newDate, setNewDate] = useState<any>(null);

	const returnAction = t("universityAccord.uploadedFile");

	const onCheckboxChange = (event: any, id: string) => {
		updateSkipOfferValidationStatus(id)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				openNotification(
					t("documents.changeStatus"),
					t("documents.statusChangedSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.statusChangedFailed"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const openProfileErrorNotification = (ex: any) => {
		if (ex.status && ex.status === StatusCodes.NOT_FOUND) {
			openNotification(
				t("account.error"),
				t("account.notFoundProfile"),
				NOTIFICATION_TYPES.ERROR
			);
		} else {
			openNotification(
				t("account.error"),
				t("account.unknownError"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	};

	const downloadFail = (_err: any) => {
		openNotification(
			t("universityAccord.error"),
			t("universityAccord.downloadError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const deleteCompanyDocument = (documentId: string) => {
		deleteDocument(documentId)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				setModalVisibility(false);
				setSelectedDocumentId(undefined);
				openNotification(
					t("documents.deleteDocument"),
					t("documents.deleteSuccessfully"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.deleteError"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const columns = [
		{
			title: t("documents.documentType"),
			dataIndex: "documentType",
			key: "documentType",
			fixed: "left" as "left",
			width: 160,
			// sorter: (a: CompanyDocumentDTO, b: CompanyDocumentDTO) => {
			// 	if (!a.documentType || !b.documentType) return -1;
			// 	return a.documentType.localeCompare(b.documentType);
			// },
			render: (text: string | undefined, record: any) => {
				return <span>{t("documents." + record.documentType)}</span>;
			},
		},
		{
			title: t("documents.faculty"),
			dataIndex: "facultyNameRo",
			key: "facultyNameRo",
			width: 200,
			// sorter: (a: CompanyDocumentDTO, b: CompanyDocumentDTO) => {
			// 	if (i18n.language === "ro") {
			// 		if (!a.facultyNameRo || !b.facultyNameRo) return -1;
			// 		return a.facultyNameRo.localeCompare(b.facultyNameRo);
			// 	} else {
			// 		if (!a.facultyNameEn || !b.facultyNameEn) return -1;
			// 		return a.facultyNameEn.localeCompare(b.facultyNameEn);
			// 	}
			// },
		},
		{
			title: t("documents.uploadDate"),
			dataIndex: "generationDate",
			key: "generationDate",
			width: 130,
			// sorter: (a: CompanyDocumentDTO, b: CompanyDocumentDTO) => {
			// 	if (!a.generationDate || !b.generationDate) return -1;
			// 	return a.generationDate < b.generationDate ? -1 : 1;
			// },
			render: (text: string | undefined, record: any) => {
				return <span>{dayjs(record.generationDate).format("DD.MM.YYYY")}</span>;
			},
		},
		{
			title: t("documents.expirationDate"),
			dataIndex: "expirationDate",
			key: "expirationDate",
			width: 130,
			render: (text: string | undefined, record: any) => {
				if (record.isExpirationApplicable) {
					return (
						<span>
							{record.expirationDate == null
								? t("documents.expirationDateNotSet")
								: dayjs(record.expirationDate).format("DD.MM.YYYY") +
								  " - " +
								  (dayjs(record.expirationDate).isBefore(dayjs())
										? t("documents.expiredStatus")
										: t("documents.activeStatus"))}
						</span>
					);
				} else {
					return <span>{t("documents.notApplicable")}</span>;
				}
			},
		},
		{
			title: t("documents.status"),
			dataIndex: "documentStatus",
			key: "documentStatus",
			width: 130,
			// sorter: (a: CompanyDocumentDTO, b: CompanyDocumentDTO) => {
			// 	if (!a.documentStatus || !b.documentStatus) return -1;
			// 	return a.documentStatus.localeCompare(b.documentStatus);
			// },
			render: (text: string | undefined, record: any) => {
				if (record.documentType === DocumentTypeEnum.PracticeProtocol) {
					return <span>{t("documents." + record.documentStatus)}</span>;
				} else {
					return <span>{t("documents.notApplicable")}</span>;
				}
			},
		},
		{
			title: t("documents.canPublish"),
			dataIndex: "skipOfferValidation",
			key: "skipOfferValidation",
			width: 130,
			render: (text: string | undefined, record: any) => {
				if (record.documentType === DocumentTypeEnum.PracticeProtocol) {
					return getUserId().toString() === record.documentOwner ||
						record.facultyId == adminData?.faculty?.id ? (
						<Checkbox
							checked={record.skipOfferValidation}
							disabled={
								getUserId().toString() !== record.documentOwner &&
								record.facultyId != adminData?.faculty?.id
							}
							onChange={(event: any) => onCheckboxChange(event, record.id)}
						/>
					) : (
						<span>
							{record.skipOfferValidation
								? t("documents.yes")
								: t("documents.no")}
						</span>
					);
				} else {
					return <span>{t("documents.notApplicable")}</span>;
				}
			},
		},
		{
			title: t("documents.actions"),
			dataIndex: "actions",
			key: "actions",
			fixed: "right" as "right",
			width: 105,
			render: (text: string | undefined, record: any) => (
				<div className={styles.modalContainer} key={"actions-" + record.id}>
					<Space>
						<div className={styles.modalBtnContainer}>
							<CustomButton
								style={{
									background: "transparent",
									border: "none",
									outline: "none",
									color: theme.black,
									boxShadow: "none",
									padding: 0,
								}}
								title={t("documents.download")}
								onClick={() =>
									downloadFile(
										downloadDocument(record.id!),
										t("documents." + record.documentType) +
											"_" +
											record.facultyNameRo +
											"_" +
											companyProfile?.name
									).catch(downloadFail)
								}
							>
								<FontAwesomeIcon
									icon={solid("download")}
									style={{ fontSize: "15px" }}
								/>
							</CustomButton>
						</div>
						{(isAdmin ||
							(isFacultyAdmin &&
								record.facultyId === adminData?.faculty?.id)) &&
						!isCompany ? (
							<div className={styles.modalBtnContainer}>
								<CustomButton
									fontSize={"0.9rem"}
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setModalVisibility(true);
									}}
									icon={
										<FontAwesomeIcon
											icon={solid("trash")}
											style={{ paddingRight: "5%" }}
										/>
									}
									title={t("documents.delete")}
								/>
								<ConfirmationModal
									modalText={t("documents.deleteWarning")}
									handleFunction={() => {
										deleteCompanyDocument(selectedDocumentId);
									}}
									modalVisibility={modalVisibility}
									title=""
									changeModalVisibility={() => setModalVisibility(false)}
									spinning={spinning}
								/>
							</div>
						) : null}
						{(getUserId().toString() === record.documentOwner &&
							isCompany &&
							record.documentStatus !== CompanyDocumentStatusEnum.Finalized) ||
						(!isCompany && record.facultyId == adminData?.faculty?.id) ? (
							<div className={styles.modalBtnContainer}>
								<CustomButton
									fontSize={"0.9rem"}
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setSelectedFaculty(record.facultyId);
										setDocumentType("practiceProtocol");
										setIsModalVisible(true);
									}}
									icon={
										<FontAwesomeIcon
											icon={solid("upload")}
											style={{ paddingRight: "5%" }}
										/>
									}
									title={t("documents.uploadDocumentTooltip")}
								/>
							</div>
						) : null}
						{record.documentStatus === CompanyDocumentStatusEnum.InProgress &&
						record.facultyId === adminData?.faculty?.id ? (
							<div className={styles.modalBtnContainer}>
								<CustomButton
									fontSize={"0.9rem"}
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setIsRejectModalVisible(true);
									}}
									icon={
										<FontAwesomeIcon
											icon={solid("ban")}
											style={{ paddingRight: "5%" }}
										/>
									}
									title={t("documents.rejectDocument")}
								/>
							</div>
						) : null}
						{(isAdmin ||
							(isFacultyAdmin &&
								record.facultyId === adminData?.faculty?.id)) &&
						!isCompany ? (
							<div className={styles.modalBtnContainer}>
								<Tooltip
									title={
										record.isExpirationApplicable
											? t("documents.updateExpiration")
											: t("documents.expirationDateNotAllowed")
									}
								>
									<span style={{ display: "inline-block" }}>
										<CustomButton
											style={{
												background: "transparent",
												border: "none",
												color: theme.black,
												boxShadow: "none",
												padding: 0,
												opacity: record.isExpirationApplicable ? 1 : 0.5,
											}}
											disabled={!record.isExpirationApplicable}
											onClick={() => {
												if (record.isExpirationApplicable) {
													setUpdateDoc(record);
													setNewDate(
														record.expirationDate
															? dayjs(record.expirationDate)
															: null
													);
												}
											}}
											icon={
												<FontAwesomeIcon
													icon={solid("file-pen")}
													style={{ paddingRight: "5%" }}
												/>
											}
										/>
									</span>
								</Tooltip>
								<Modal
									open={!!updateDoc}
									title={t("documents.updateExpiration")}
									onCancel={() => setUpdateDoc(null)}
									onOk={async () => {
										try {
											await updateDocumentExpiration(
												updateDoc.id,
												newDate?.toDate()
											);
											openNotification(
												t("documents.updateExpiration"),
												t("documents.updateSuccess"),
												NOTIFICATION_TYPES.SUCCESS
											);
											setUpdateDoc(null);
											await queryClient.invalidateQueries(getDocuments);
										} catch {
											openNotification(
												t("documents.updateExpiration"),
												t("documents.updateFailed"),
												NOTIFICATION_TYPES.ERROR
											);
										}
									}}
								>
									<DatePicker
										style={{ width: "100%" }}
										value={newDate}
										onChange={(d) => setNewDate(d)}
									/>
								</Modal>
							</div>
						) : null}
					</Space>
				</div>
			),
		},
	];

	const { data: professorProfile } = useQuery(
		[getProfessor, location, isProfessor],
		async () =>
			location.state && location.state.userType === Role.Professor
				? getProfessorById(location.state.id)
				: !location.state && isProfessor
				? getProfessorById(getUserId())
				: null,
		{
			onError: openProfileErrorNotification,
		}
	);

	const { data: companyUser } = useQuery(
		[getCompanyUser, location, isCompany, companyId],
		async () =>
			isCompany && (!location.state || location.state?.id === companyId)
				? getCompanyUserById(getUserId())
				: null,
		{
			onError: openProfileErrorNotification,
		}
	);

	const { data: companyProfile } = useQuery(
		[getCompany, location, isCompany, companyId],
		async () =>
			location.state && location.state.userType === Role.Company
				? getCompanyById(location.state.id)
				: !location.state && isCompany
				? getCompanyById(companyId)
				: null,
		{
			onError: openProfileErrorNotification,
		}
	);

	const data = professorProfile
		? {
				id: professorProfile.id,
				image: professorProfile.fullResImage ?? professorProfile.lowResImage,
				departmentNameRo: professorProfile.departmentNameRo,
				departmentNameEn: professorProfile.departmentNameEn,
				phone: professorProfile.phone,
				email: professorProfile.email,
				personalEmail: professorProfile.personalEmail,
				description: professorProfile.description,
				name: professorProfile.name,
				professionalTitle: professorProfile.professionalTitle,
				siteUrl: professorProfile.siteUrl,
		  }
		: companyProfile
		? {
				id: companyProfile.id,
				logo: companyProfile.fullResLogo ?? companyProfile.lowResLogo,
				domainOfActivity: companyProfile.domainOfActivity,
				phone: companyProfile.phone,
				email: companyProfile.email,
				description: companyProfile.description,
				name: companyProfile.commercialName,
				linkedInUrl: companyProfile.linkedInUrl,
				siteUrl: companyProfile.siteUrl,
		  }
		: null;

	const openNotificationErrorFetchDocuments = (_ex: any) => {
		openNotification(
			t("usersText.errorTexts.error"),
			t("documents.documentsFetchFailed"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const fetchDataErrorNotification = (ex: any) => {
		if (ex.status && ex.status === StatusCodes.NOT_FOUND) {
			openNotification(
				t("account.error"),
				t("account.fetchUserDataError"),
				NOTIFICATION_TYPES.ERROR
			);
		} else {
			openNotification(
				t("account.error"),
				t("account.unknownError"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	};

	const { data: adminData } = useQuery(
		[getProfessor, isFacultyAdmin],
		async () => (isFacultyAdmin ? getProfessorById(getUserId()) : null),
		{
			onError: fetchDataErrorNotification,
		}
	);

	const { data: documents, isLoading: loading } = useQuery(
		[
			getDocuments,
			location.state?.userType,
			location.state?.id,
			documentTypes,
			currentPage,
			pageSize,
			isFacultyAdmin,
			adminData?.faculty?.id,
			validity,
		],
		() => {
			if (
				isFacultyAdmin &&
				location.state &&
				location.state.userType === Role.Company
			) {
				return getCompanyDocuments(
					location.state?.id,
					documentTypes,
					currentPage,
					pageSize,
					validity
				);
			} else {
				return null;
			}
		},
		{
			onError: openNotificationErrorFetchDocuments,
			onSuccess: (data) => {
				if (data) {
					if (data?.data !== null && data?.data !== undefined) {
						const hasProtocolForUserFaculty = data?.data.some(
							(e) =>
								e.documentType === DocumentTypeEnum.PracticeProtocol &&
								e.facultyId === adminData?.faculty?.id &&
								!(e.expirationDate && dayjs(e.expirationDate).isBefore(dayjs()))
						);

						if (hasProtocolForUserFaculty) {
							setDocumentsOptions([
								{
									label: t("documents.collaborationAccord"),
									value: DocumentTypeEnum.CollaborationAccord,
								},
							]);
						} else {
							setDocumentsOptions([
								{
									label: t("documents.practiceProtocol"),
									value: DocumentTypeEnum.PracticeProtocol,
								},
								{
									label: t("documents.collaborationAccord"),
									value: DocumentTypeEnum.CollaborationAccord,
								},
							]);
						}

						if (adminData?.faculty?.id) {
							setAdminFaculties([adminData.faculty.id]);
						}
					}
				}
			},
		}
	);

	const handleShow = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setDocumentType(null);
		setSelectedFaculty(undefined);
		setFile(undefined);
		setSelectedDocumentId(undefined);
		setExpiresAt(null);
		setIsModalVisible(false);
		setIsRejectModalVisible(false);
	};

	const uploadDocument = (
		companyId: string,
		file: Blob,
		facultyId?: string,
		documentId?: string,
		documentType?: DocumentTypeEnum,
		expiresAt?: Date
	) => {
		addNewDocument(
			companyId,
			file,
			facultyId,
			documentId,
			documentType,
			expiresAt
		)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				setDocumentType(null);
				setSelectedFaculty(undefined);
				setSpinning(false);
				setFile(undefined);
				setExpiresAt(null);
				setSelectedDocumentId(undefined);
				setIsModalVisible(false);
				openNotification(
					t("documents.uploadedDocument"),
					t("documents.uploadSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				setSpinning(false);
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.uploadFail"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const handleOk = () => {
		setSpinning(true);
		if (
			location.state?.userType == Role.Company &&
			location.state?.id &&
			file !== undefined
		) {
			uploadDocument(
				location.state?.id,
				file,
				selectedFaculty,
				selectedDocumentId,
				documentType,
				(documentType === DocumentTypeEnum.PracticeProtocol ||
					documentType === "practiceProtocol") &&
					expiresAt
					? expiresAt.toDate()
					: undefined
			);
		} else {
			setSpinning(false);
		}
	};

	const onChange = (event: string) => {
		setDocumentType(event);
	};

	const rejectCompanyDocument = (documentId: string, message: string) => {
		setSpinning(true);

		rejectDocument(documentId, message)
			.then(() => {
				openNotification(
					t("documents.documentRejection"),
					t("documents.documentRejectionSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);

				setRejectionMessage("");
				setIsRejectModalVisible(false);
			})
			.catch(async (error) => {
				const errorMessage = await getErrorFromResponse(error);

				let notificationMessage;

				switch (errorMessage?.code) {
					case ErrorCodes.DocumentNotFound:
						notificationMessage = t("documents.documentNotFound");
						break;
					case ErrorCodes.Forbidden:
						notificationMessage = t("documents.notAllowed");
						break;
					default:
						notificationMessage = t("documents.documentRejectionError");
						break;
				}

				openNotification(
					t("documents.documentRejection"),
					notificationMessage,
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
			});
	};

	let locale = {
		emptyText: t("tableText.noDocuments"),
	};

	const handleListChange = (current: number, pageSize: number) => {
		setCurrent(() => current);
		setPageSize(() => pageSize);
	};

	const goToEdit = () => history.push("/contul-meu");

	if (!isUserLogged() && !location.state) {
		return <Redirect to="/404" />;
	} else {
		return (
			<DashboardLayout>
				<Spin
					size="large"
					spinning={!professorProfile && !companyProfile}
					tip={t("account.loading")}
				>
					<div className="flex items-center gap-2 mt-2 ml-2">
						<ArrowDownOutlined
							onClick={() => history.goBack()}
							className="text-4xl rotate-90 text-primary"
						/>
					</div>
					<div className="flex flex-col items-center pt-20 pb-10">
						<div className="relative w-full py-10 bg-primary">
							<div className="relative grid md:grid-rows-[auto_1fr] grid-cols-1 md:grid-cols-[218px_auto] gap-12 max-w-[727px] w-full mx-auto">
								<div className="flex flex-col items-center row-span-2 gap-6">
									<Avatar
										size={218}
										src={data?.image || data?.logo}
										icon={<UserOutlined />}
									/>

									{(companyUser?.id === getUserId() ||
										professorProfile?.id === getUserId()) &&
									((isCompany &&
										companyUser &&
										(companyUser.isRepresentative || companyUser.isAdmin)) ||
										isProfessor) ? (
										<Button className="bg-primary-faded" onClick={goToEdit}>
											{t("account.edit")}
										</Button>
									) : null}
									{isAdmin &&
									professorProfile !== undefined &&
									professorProfile?.id !== getUserId() &&
									location?.state?.userType !== Role.Company ? (
										<Link
											to={{
												pathname: `/contul-meu/` + location?.state?.id,
											}}
										>
											<Button className="bg-primary-faded">
												{t("account.edit")}
											</Button>
										</Link>
									) : null}
									{isAdmin && location?.state?.userType === Role.Company ? (
										<Link
											to={{
												pathname: `/cont-companie/` + location?.state?.id,
											}}
										>
											<Button className="bg-primary-faded">
												{t("account.edit")}
											</Button>
										</Link>
									) : null}
								</div>

								<div className="flex items-center justify-center order-first gap-2 text-2xl font-bold text-white md:order-none md:justify-start">
									{data?.professionalTitle &&
									data?.professionalTitle !==
										ProfessionalTitleEnum.NotApplicable
										? t("professionalTitle." + data?.professionalTitle)
										: ""}
									<span>{data?.name}</span>

									{data?.email &&
										location.state?.userType !== Role.Company &&
										!isCompany &&
										data.id !== getUserId() && (
											<Button
												type="text"
												href={
													"https://teams.microsoft.com/l/chat/0/0?users=" +
													data?.email
												}
												target="_blank"
												rel="noopener noreferrer"
												style={{
													backgroundColor: "transparent",
													float: "left",
													padding: "0",
												}}
												title={t("students.teams")}
											>
												<FontAwesomeIcon
													icon={solid("comment")}
													className="text-2xl"
												/>
											</Button>
										)}

									{data?.siteUrl && (
										<Button
											type="text"
											href={data?.siteUrl}
											target="_blank"
											rel="noreferrer"
											style={{
												backgroundColor: "transparent",
												float: "left",
												padding: "0",
											}}
											title={t("profile.siteUrl")}
										>
											<FontAwesomeIcon
												icon={solid("link")}
												className="text-2xl text-white"
											/>
										</Button>
									)}

									{data?.linkedInUrl && (
										<Button
											type="text"
											href={data?.linkedInUrl!}
											target="_blank"
											rel="noreferrer"
											style={{
												backgroundColor: "transparent",
												padding: "0",
											}}
											title={t("students.linkedinUrl")}
										>
											<FontAwesomeIcon
												icon={brands("linkedin")}
												className="text-2xl text-white"
											/>
										</Button>
									)}
								</div>

								<div className="grid order-last grid-cols-1 gap-4 mx-auto text-center text-white md:grid-cols-2 w-fit md:order-none md:mx-0 md:text-left">
									{i18n.language === "ro" ? (
										data?.departmentNameRo ? (
											<>
												<div className="self-center font-bold">Departament</div>
												<div className="self-center">
													{data?.departmentNameRo}
												</div>
											</>
										) : null
									) : data?.departmentNameEn ? (
										<>
											<div className="self-center font-bold">Department</div>
											<div className="self-center">
												{data?.departmentNameEn}
											</div>
										</>
									) : null}

									{data?.domainOfActivity && (
										<>
											<div className="self-center font-bold">
												{t("signupText.activityDomain")}
											</div>
											<div className="self-center">
												{data?.domainOfActivity}
											</div>
										</>
									)}

									{data?.phone && (
										<>
											<div className="self-center font-bold">
												{t("account.phone")}
											</div>
											<div className="self-center">{data?.phone}</div>
										</>
									)}

									{data?.email && (
										<>
											<div className="self-center font-bold">
												{t("account.email")}
											</div>
											<div className="flex items-center self-center">
												{data?.email}
												<Button
													type="text"
													style={{
														backgroundColor: "transparent",
													}}
													onClick={() => {
														navigator.clipboard
															.writeText(data?.email || "")
															.then(() => {
																openNotification(
																	t("profile.emailCopied"),
																	t("profile.emailCopiedMessage"),
																	NOTIFICATION_TYPES.SUCCESS
																);
															});
													}}
												>
													<FontAwesomeIcon
														icon={solid("copy")}
														className="text-2xl text-white"
														title={t("account.copyToClipboard")}
													/>
												</Button>
											</div>
										</>
									)}

									{data?.personalEmail && (
										<>
											{" "}
											<div className="self-center font-bold">
												{t("account.email")}
											</div>
											<div className="flex items-center self-center">
												{data?.personalEmail}
												<Button
													type="text"
													style={{
														backgroundColor: "transparent",
													}}
													onClick={() => {
														navigator.clipboard
															.writeText(data?.personalEmail || "")
															.then(() => {
																openNotification(
																	t("profile.emailCopied"),
																	t("profile.emailCopiedMessage"),
																	NOTIFICATION_TYPES.SUCCESS
																);
															});
													}}
												>
													<FontAwesomeIcon
														icon={solid("copy")}
														className="text-2xl text-white"
														title={t("account.copyToClipboard")}
													/>
												</Button>
											</div>
										</>
									)}
								</div>
							</div>
						</div>

						<div
							className="max-w-[727px] w-full mx-auto px-4"
							dangerouslySetInnerHTML={{ __html: data?.description || "" }}
						></div>

						{isFacultyAdmin &&
							location.state &&
							location.state.userType === Role.Company && (
								<div className="w-full px-4">
									<h2>{t("documents.documents")}</h2>

									<Button
										onClick={handleShow}
										className="mb-6"
										disabled={adminFaculties?.length === 0}
									>
										{t("documents.uploadDocument")}
									</Button>

									<Modal
										open={isModalVisible}
										maskClosable={!spinning}
										onCancel={handleCancel}
										onOk={handleOk}
										title={""}
										width={window.innerWidth > 1215 ? "50%" : "80%"}
										footer={
											<CustomModalFooter
												handleClose={handleCancel}
												spinning={spinning}
												handleSave={handleOk}
												confirmButtonName={t("account.confirm")}
											/>
										}
									>
										<Spin spinning={spinning}>
											<p>{t("documents.selectDocumentText")}</p>
											<div className="flex flex-col gap-6">
												{selectedDocumentId === undefined && (
													<Select
														className="w-full"
														value={documentType}
														onChange={onChange}
														placeholder={t("documents.selectDocumentType")}
														options={documentsOptions}
														allowClear
													/>
												)}
												{!isCompany &&
													(documentType === DocumentTypeEnum.PracticeProtocol ||
														documentType === "practiceProtocol") && (
														<DatePicker
															style={{ width: "100%" }}
															value={expiresAt}
															onChange={(d) => setExpiresAt(d)}
															placeholder={t("documents.selectExpirationDate")}
														/>
													)}

												<Upload
													accept=".pdf"
													action={(uploaded) => {
														setFile(uploaded);
														return returnAction;
													}}
													showUploadList={true}
													multiple={false}
													customRequest={({ onSuccess }) => {
														onSuccess && onSuccess("");
													}}
												>
													<Button
														disabled={documentType === null}
														icon={<FontAwesomeIcon icon={solid("upload")} />}
														title={t("documents.uploadPdf")}
														style={{ marginTop: "2rem" }}
													>
														&nbsp;{t("documents.upload")}
													</Button>
												</Upload>
											</div>
										</Spin>
									</Modal>
									<Modal
										open={isRejectModalVisible}
										onCancel={handleCancel}
										onOk={() =>
											rejectCompanyDocument(
												selectedDocumentId,
												rejectionMessage
											)
										}
										title={t("documents.documentRejection")}
										width={window.innerWidth > 1215 ? "50%" : "80%"}
										footer={
											<CustomModalFooter
												handleClose={handleCancel}
												spinning={spinning}
												handleSave={() =>
													rejectCompanyDocument(
														selectedDocumentId,
														rejectionMessage
													)
												}
												confirmButtonName={t("account.confirm")}
											/>
										}
									>
										<Spin spinning={spinning}>
											<p>{t("documents.rejectDocumentMessage")}</p>

											<CustomEditor
												content={rejectionMessage}
												onEditorChange={(newValue: string) =>
													setRejectionMessage(newValue)
												}
											/>
										</Spin>
									</Modal>

									<div className="flex flex-col px-4 py-10">
										<div className="flex flex-col gap-10">
											<div className="flex flex-col items-center justify-between md:flex-row">
												<Typography.Title level={3} className="my-0">
													{documents?.totalCount} {t("filtering.results")}
												</Typography.Title>
												{!loading && (
													<Pagination
														defaultCurrent={currentPage}
														defaultPageSize={pageSize}
														total={documents?.totalCount}
														onChange={handleListChange}
														locale={{ items_per_page: t("pagination") }}
													/>
												)}
											</div>

											<Table
												locale={locale}
												columns={columns}
												dataSource={documents?.data ?? []}
												pagination={false}
												scroll={{ x: "calc(700px + 50%)", y: 420 }}
												rowKey={(record) => record.id!}
											/>
										</div>
									</div>
								</div>
							)}

						<div className="w-full px-4">
							{isFacultyAdmin &&
								location.state &&
								location.state.userType === Role.Company && (
									<PracticeSupervisorTable companyId={location.state.id} />
								)}
							{(isFacultyAdmin || isDean) &&
								// own profile -> an admin/dean who also is an internship admin
								((!location.state && isInternshipAdmin) ||
									// someone else's profile who is an internship admin
									(location.state &&
										location.state.userType === Role.Professor &&
										professorProfile?.roles?.includes(
											Role.InternshipAdmin
										))) && (
									<SupervisedStudyProgramsTable
										supervisorId={
											location.state ? location.state.id : getUserId()
										}
									/>
								)}
							{(isCompany || isAdmin) && (
								<FacultiesOfInterestTable
									companyId={location.state ? location.state.id : getUserId()}
								/>
							)}
							{(isAdmin || isDean) &&
								location.state &&
								location.state.userType === Role.Company && (
									<ColaborationHistoryTable
										companyId={location.state ? location.state.id : getUserId()}
									/>
								)}
						</div>
					</div>
				</Spin>
			</DashboardLayout>
		);
	}
};

export default Profile;
